// Services
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import { RequestStatusFragment, VehicleFragment } from "./fragments/dealerInventory";

// Types
import { Nullish } from "@Common/interfaces/types";
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { IMappedTransferRequest, ITransferRequestInput, IVehicle } from "@/DataPages/components/Inventory/types/dealerTrades";

export async function createTransferRequestStatus(locationId: string, input: IMappedTransferRequest[]) {
	const query = gql`
		mutation createTransferRequestStatus($locationId: String!, $input: [CreateTransferRequestStatusInput], $source: String) {
			createTransferRequestStatus(locationId: $locationId, input: $input, source: $source) {
				destinationLocationId
				event
				originLocationId
				transferRequests {
					_id
					requestId
					swapId
					originLocationId
					destinationLocationId
					requestedByLocationId
					requestStatus {
						...RequestStatus
					}
					transferType
					vehicle {
						...Vehicle
					}
				}
			}
		}
		${RequestStatusFragment}
		${VehicleFragment}
	`;

	const variables = {
		locationId,
		input,
		source: "CLIENT",
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createTransferRequestStatus;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export const mutateTransferRequestStatus = async ({ locationId, trades }: { locationId: string; trades: ITransferRequestInput[] }) => {
	const query = gql`
		mutation updateTransferStatus($locationId: String!, $input: [TradesInput]!) {
			updateTransferStatus(locationId: $locationId, input: $input) {
				event
				transferRequests {
					_id
					destinationLocationId
					originLocationId
					requestId
					swapId
					requestedByLocationId
					transferType
					isBlocked
					vehicle {
						...Vehicle
					}
					requestStatus {
						...RequestStatus
					}
				}
			}
		}
		${RequestStatusFragment}
		${VehicleFragment}
	`;

	const variables = {
		locationId,
		input: trades,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateTransferStatus;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
};

export const editTransferRequest = async ({
	_id,
	requestId,
	swapId,
	locationId,
	input,
}: {
	_id: string;
	requestId: string;
	swapId?: string;
	locationId: string;
	input: IVehicle;
}) => {
	const query = gql`
		mutation editTransferRequest(
			$_id: String!
			$locationId: String!
			$requestId: String!
			$input: VehicleDetailsInput!
			$swapId: String
		) {
			editTransferRequest(_id: $_id, locationId: $locationId, requestId: $requestId, input: $input, swapId: $swapId) {
				destinationLocationId
				event
				originLocationId
				transferRequests {
					_id
					destinationLocationId
					originLocationId
					requestId
					swapId
					requestStatus {
						...RequestStatus
					}
					requestedByLocationId
					transferType
					vehicle {
						...Vehicle
					}
				}
			}
		}
		${RequestStatusFragment}
		${VehicleFragment}
	`;

	const variables = { _id, locationId, requestId, swapId, input };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.editTransferRequest;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
};

type TTransferGPSDeviceInput = {
	locationId: string;
	inventoryId: string;
	eventSource: "CLIENT" | "TRANSFER_PROCESSING";
};
type TTransferGPSDeviceResponse = {
	locationId: string;
	inventoryId: string;
	isSuccess: boolean;
	vinNumber: string;
};
export async function transferGPSDevice({
	locationId,
	inventoryId,
	eventSource,
}: TTransferGPSDeviceInput): Promise<Nullish<TTransferGPSDeviceResponse>> {
	const query = gql`
		mutation transferGPSDevice($locationId: String!, $inventoryId: String!, $eventSource: String!) {
			transferGPSDevice(locationId: $locationId, inventoryId: $inventoryId, eventSource: $eventSource) {
				locationId
				inventoryId
				isSuccess
				vinNumber
			}
		}
	`;

	const variables: TTransferGPSDeviceInput = {
		locationId,
		inventoryId,
		eventSource,
	};

	try {
		const response: GraphqlQueryResponse<"transferGPSDevice", TTransferGPSDeviceResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.transferGPSDevice ?? null;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function requestConsumerRegistrationStatusIngestion(locationId: string) {
	const query = gql`
		mutation requestConsumerRegistrationStatusIngestion($locationId: String!) {
			requestConsumerRegistrationStatusIngestion(locationId: $locationId)
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.requestConsumerRegistrationStatusIngestion;
	} catch (err) {
		errorLogger(err as Error, {
			function: "requestConsumerRegistrationStatusIngestion",
			message: "Failed to request Consumer Registration Status ingestion",
			variables,
		});
		return null;
	}
}
